'use client'
import { Teams } from '@glovo/organisation'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { useCourierChat } from '../../useCourierChat'
import styles from './CourierChatIframe.module.scss'
import {
  HelpCenterMessageType,
  HostMessageType,
} from '@/help-center/hooks/use-bridge-communication/types'
import { useBridgeCommunication } from '@/help-center/hooks/use-bridge-communication/use-bridge-communication'
import { HelpCenterPanel } from '@/help-center/ui/help-center-panel/HelpCenterPanel'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling, withErrorBoundary } from '@/tools/o11y/client'

const CourierChatIframeComponent: FC = () => {
  const { t } = useLocalisation()

  const {
    fetchCourierChatIframeUrl,
    closeCourierChat,
    isCourierChatVisible,
    courierChatIframeUrl,
    chatConfig,
  } = useCourierChat()

  const { startBridgeCommunication, closeBridgeCommunication, addMessageHandler, sendMessage } =
    useBridgeCommunication()

  const { withErrorTracking, execWithErrorTracking } = useErrorHandling()

  const [isLoading, setIsLoading] = useState(false)
  const [hasLoadFailed, setHasLoadFailed] = useState(false)

  const initialiseCourierChatIframe = async () => {
    setIsLoading(true)
    setHasLoadFailed(false)
    try {
      const iframeUrl = await fetchCourierChatIframeUrl()

      if (iframeUrl) {
        addMessageHandler(HelpCenterMessageType.HELPCENTER_ASKS_FOR_SHUTDOWN, () => {
          closeCourierChat()
        })

        addMessageHandler(HelpCenterMessageType.CLIENT_ASKS_FOR_CHAT_CONFIG, () => {
          sendMessage({
            type: HostMessageType.HOST_APP_PROVIDES_CHAT_CONFIG,
            payload: chatConfig,
          })
        })
        startBridgeCommunication(iframeUrl)
      }
    } catch (error) {
      setHasLoadFailed(true)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    closeCourierChat()
  }

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (isCourierChatVisible) {
        await initialiseCourierChatIframe()
      } else {
        closeBridgeCommunication()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCourierChatVisible])

  useEffect(
    // eslint-disable-next-line o11y-guard/error-tracking
    () =>
      withErrorTracking(() => {
        closeBridgeCommunication()
      }),
    [closeBridgeCommunication, withErrorTracking]
  )

  return (
    <HelpCenterPanel
      hasLoadFailed={hasLoadFailed}
      isLoading={isLoading}
      title={t('support.courierChat')}
      onClose={handleClose}
    >
      <iframe
        className={styles.iframe}
        src={courierChatIframeUrl ? courierChatIframeUrl : undefined}
        title={t('support.courierChat')}
      />
    </HelpCenterPanel>
  )
}

export const CourierChatIframe = withErrorBoundary(CourierChatIframeComponent, {
  id: 'help_center.courier_chat',
  owner: Teams.WEBX,
})
